import { Plus, Trash2, Lock, X, Plane } from "lucide-react";
import SelectMultipleWithSearch from "../Admin/Common/SelectMultipleWithSearch";
import SelectItem from "../Admin/Common/SelectItem";
import { memo, useState, useCallback, useMemo } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { OPERATION_TYPE } from "../../utils/constant";
import CopyFromMenu from "./CopyFromMenu";

// Segment row is memoized — only re-renders when its own data changes
const SegmentRow = memo(
  ({
    seg,
    index,
    isFirst,
    isLast,
    isOnly,
    airLineList,
    airPort,
    originAirport,
    destAirport,
    mainDeparture,
    mainArrival,
    errors,
    onUpdate,
    onRemove,
  }) => {
    const set = useCallback(
      (field, value) => onUpdate(index, field, value),
      [index, onUpdate],
    );

    return (
      <div className="rounded-xl border border-gray-200 overflow-hidden">
        {/* Segment header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
              {index + 1}
            </span>
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Segment {index + 1}
            </span>
          </div>
          {!isFirst && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded-md transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              Remove
            </button>
          )}
        </div>

        <div className="p-4 space-y-3">
          {/* Row 1: Carrier / Op. Type / Origin / Destination */}
          <div className="grid grid-cols-4 gap-3">
            {/* Carrier */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Carrier
              </label>
              <SelectMultipleWithSearch
                options={airLineList}
                width="100%"
                name={`seg-${index}-carrier`}
                value={seg.carrier || undefined}
                setFieldValue={(_, val) => set("carrier", val)}
                handleChange={() => {}}
              />
              {errors?.carrier && (
                <p className="text-red-500 text-xs mt-0.5">{errors.carrier}</p>
              )}
            </div>

            {/* Op. Type */}
            {/* Op. Type */}
<div>
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
    Operation Type
  </label>
  <div className="[&_.ant-select]:!w-full">
    <SelectItem
      options={OPERATION_TYPE}
      width="100%"
      name={`seg-${index}-operationType`}
      value={seg.operationType || undefined}
      placeholder="Type"
      setFieldValue={(_, val) => set("operationType", val)}
      handleChange={() => {}}
    />
  </div>
</div>

            {/* Origin */}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Origin
                </label>
                {isFirst && <Lock className="w-3 h-3 text-amber-400" />}
              </div>
              {isFirst ? (
                <input
                  readOnly
                  value={originAirport || seg.origin || ""}
                  className="w-full text-sm border border-amber-200 rounded-lg px-3 h-[34px] text-gray-600 bg-amber-50 cursor-not-allowed"
                />
              ) : (
                <SelectMultipleWithSearch
                  options={airPort}
                  width="w-full"
                  name={`seg-${index}-origin`}
                  value={seg.origin || undefined}
                  setFieldValue={(_, val) => set("origin", val)}
                  handleChange={() => {}}
                />
              )}
              {errors?.origin && (
                <p className="text-red-500 text-xs mt-0.5">{errors.origin}</p>
              )}
            </div>

            {/* Destination */}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Destination
                </label>
                {isLast && <Lock className="w-3 h-3 text-amber-400" />}
              </div>
              {isLast ? (
                <input
                  readOnly
                  value={destAirport || seg.destination || ""}
                  className="w-full text-sm border border-amber-200 rounded-lg px-3 h-[34px] text-gray-600 bg-amber-50 cursor-not-allowed"
                />
              ) : (
                <SelectMultipleWithSearch
                  options={airPort}
                  width="w-full"
                  name={`seg-${index}-dest`}
                  value={seg.destination || undefined}
                  setFieldValue={(_, val) => set("destination", val)}
                  handleChange={() => {}}
                />
              )}
              {errors?.destination && (
                <p className="text-red-500 text-xs mt-0.5">
                  {errors.destination}
                </p>
              )}
            </div>
          </div>

          {/* Row 2: Departure / Arrival / Aircraft / Flight */}
          <div className="grid grid-cols-4 gap-3">
            {/* Departure */}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Departure
                </label>
                {isFirst && <Lock className="w-3 h-3 text-amber-400" />}
              </div>
              <DatePicker
                value={seg.departure != null ? dayjs(seg.departure) : null}
                showTime={{ format: "HH:mm", showSecond: false }}
                format="YYYY-MM-DD HH:mm"
                disabled={isFirst}
                className={`w-full text-sm rounded-lg ${isFirst ? "bg-amber-50" : ""}`}
                style={{ height: 34 }}
                disabledDate={(current) =>
                  current &&
                  (current < dayjs().startOf("day") ||
                    current > dayjs().add(3, "month").endOf("day"))
                }
                onChange={(date) => {
                  set("departure", date);
                  // Clear arrival if it's now <= new departure
                  if (
                    seg.arrival &&
                    date &&
                    !dayjs(seg.arrival).isAfter(dayjs(date))
                  ) {
                    set("arrival", null);
                  }
                }}
              />
              {isFirst && (
                <small className="text-amber-400 text-xs">
                  Auto from schedule
                </small>
              )}
              {errors?.departure && (
                <p className="text-red-500 text-xs mt-0.5">{errors.departure}</p>
              )}
            </div>

            {/* Arrival */}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Arrival
                </label>
                {isLast && <Lock className="w-3 h-3 text-amber-400" />}
              </div>
              <DatePicker
                value={seg.arrival != null ? dayjs(seg.arrival) : null}
                showTime={{ format: "HH:mm", showSecond: false }}
                format="YYYY-MM-DD HH:mm"
                disabled={isLast}
                className={`w-full text-sm rounded-lg ${isLast ? "bg-amber-50" : ""}`}
                style={{ height: 34 }}
                disabledDate={(current) => {
                  if (!current) return false;
                  if (current > dayjs().add(3, "month").endOf("day")) return true;
                  if (!seg.departure) return false;
                  return current.isBefore(
                    dayjs(seg.departure).startOf("day")
                  );
                }}
                disabledTime={(current) => {
                  if (!seg.departure || !current) return {};
                  const dep = dayjs(seg.departure);
                  if (!current.isSame(dep, "day")) return {};
                  return {
                    disabledHours: () =>
                      Array.from({ length: dep.hour() }, (_, i) => i),
                    disabledMinutes: (h) =>
                      h === dep.hour()
                        ? Array.from({ length: dep.minute() + 1 }, (_, i) => i)
                        : [],
                  };
                }}
                onChange={(date) => {
                  if (
                    date &&
                    seg.departure &&
                    dayjs(date).isBefore(dayjs(seg.departure))
                  )
                    return;
                  set("arrival", date);
                }}
              />
              {isLast && (
                <small className="text-amber-400 text-xs">
                  Auto from schedule
                </small>
              )}
              {errors?.arrival && (
                <p className="text-red-500 text-xs mt-0.5">{errors.arrival}</p>
              )}
            </div>

            {/* Aircraft */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Aircraft
              </label>
              <input
                type="text"
                value={seg.aircraft || ""}
                placeholder="B787, A350"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 h-[34px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                onChange={(e) => set("aircraft", e.target.value)}
              />
              {errors?.aircraft && (
                <p className="text-red-500 text-xs mt-0.5">{errors.aircraft}</p>
              )}
            </div>

            {/* Flight */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Flight No.
              </label>
              <input
                type="text"
                value={seg.flight || ""}
                placeholder="AI-101"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 h-[34px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                onChange={(e) => set("flight", e.target.value)}
              />
              {errors?.flight && (
                <p className="text-red-500 text-xs mt-0.5">{errors.flight}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

const IteneraryForm = memo(
  ({
    formikData,
    basePath,
    option,
    carrierCode,
    copySources,
    airLineList,
    airPort,
    handleClose,
    originAirport,
    destAirport,
  }) => {
    const mainDeparture = option?.departureDateAndTime;
    const mainArrival = option?.arrivalDateAndTime;

    // Local state — completely decoupled from Formik during editing
    const [segments, setSegments] = useState(() => {
      const raw = option?.itinerary || [];
      return raw.map((seg, idx) => ({
        ...seg,
        // Apply locked values on init
        ...(idx === 0
          ? {
              origin: originAirport || seg.origin,
              departure: mainDeparture || seg.departure,
            }
          : {}),
        ...(idx === raw.length - 1
          ? {
              destination: destAirport || seg.destination,
              arrival: mainArrival || seg.arrival,
            }
          : {}),
      }));
    });

    const [errors, setErrors] = useState({});

    // Stable update/remove handlers
    const handleUpdate = useCallback((segIndex, field, value) => {
      setSegments((prev) => {
        const updated = prev.map((s, i) =>
          i === segIndex ? { ...s, [field]: value } : s,
        );
        // Auto-chain: destination of segment N → origin of segment N+1
        if (field === "destination" && segIndex < prev.length - 1) {
          updated[segIndex + 1] = { ...updated[segIndex + 1], origin: value };
        }
        return updated;
      });
    }, []);

    const handleRemove = useCallback((segIndex) => {
      setSegments((prev) => prev.filter((_, i) => i !== segIndex));
    }, []);

    // Pulls in another option's segments wholesale — carrier is reset to
    // this option's own carrier and flight numbers are cleared, since a
    // copied itinerary very often comes from a different airline/schedule.
    const handleCopy = useCallback(
      (source) => {
        const cloned = JSON.parse(JSON.stringify(source.option?.itinerary || []));
        setSegments(
          cloned.map((seg) => ({ ...seg, carrier: carrierCode || seg.carrier, flight: "" }))
        );
        setErrors({});
      },
      [carrierCode]
    );

    const handleAdd = useCallback(() => {
      setSegments((prev) => [
        ...prev,
        {
          carrier: carrierCode || "",
          operationType: "",
          origin: "",
          destination: destAirport || "",
          departure: prev[prev.length - 1]?.arrival || null,
          arrival: mainArrival || null,
          aircraft: "",
          flight: "",
        },
      ]);
    }, [carrierCode, destAirport, mainArrival]);

    // Compute final values with locked fields applied
    const finalSegments = useMemo(
      () =>
        segments.map((seg, idx) => ({
          ...seg,
          ...(idx === 0
            ? {
                origin: originAirport || seg.origin,
                departure: mainDeparture || seg.departure,
              }
            : {}),
          ...(idx === segments.length - 1
            ? {
                destination: destAirport || seg.destination,
                arrival: mainArrival || seg.arrival,
              }
            : {}),
        })),
      [segments, originAirport, destAirport, mainDeparture, mainArrival],
    );

    const validate = () => {
      const errs = {};
      finalSegments.forEach((seg, idx) => {
        if (!seg.carrier?.trim()) errs[`${idx}.carrier`] = "Required";
        if (!seg.origin?.trim()) errs[`${idx}.origin`] = "Required";
        if (!seg.destination?.trim()) errs[`${idx}.destination`] = "Required";
        if (!seg.departure) errs[`${idx}.departure`] = "Required";
        if (!seg.arrival) errs[`${idx}.arrival`] = "Required";
        if (!seg.aircraft?.trim()) errs[`${idx}.aircraft`] = "Required";
        if (!seg.flight?.trim()) errs[`${idx}.flight`] = "Required";

        // Origin must not equal destination
        if (
          seg.origin?.trim() &&
          seg.destination?.trim() &&
          seg.origin.trim() === seg.destination.trim()
        ) {
          errs[`${idx}.destination`] = "Destination cannot be same as origin";
        }

        // Departure must be before arrival
        if (seg.departure && seg.arrival && !dayjs(seg.arrival).isAfter(dayjs(seg.departure))) {
          errs[`${idx}.arrival`] = "Arrival must be after departure";
        }
      });

      // Through validation: each segment's destination must match the next segment's origin
      for (let idx = 0; idx < finalSegments.length - 1; idx++) {
        const curr = finalSegments[idx];
        const next = finalSegments[idx + 1];
        if (
          curr.destination?.trim() &&
          next.origin?.trim() &&
          curr.destination.trim() !== next.origin.trim()
        ) {
          errs[`${idx}.destination`] =
            `Through mismatch: must connect to next segment's origin (${next.origin})`;
        }
      }

      setErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const handleConfirm = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!validate()) return;
      // Write to Formik only once on confirm
      formikData.setFieldValue(`${basePath}.itinerary`, finalSegments);
      handleClose();
    };

    return (
      <div className="flex flex-col" style={{ maxHeight: "90vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Plane className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800 text-sm leading-tight">
                Flight Itinerary
              </h2>
              <p className="text-xs text-gray-400">
                {segments.length} segment{segments.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CopyFromMenu sources={copySources} onCopy={handleCopy} />
            <button
              type="button"
              onClick={handleClose}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {finalSegments.map((seg, index) => (
            <SegmentRow
              key={index}
              seg={seg}
              index={index}
              isFirst={index === 0}
              isLast={index === segments.length - 1}
              isOnly={segments.length === 1}
              airLineList={airLineList}
              airPort={airPort}
              originAirport={originAirport}
              destAirport={destAirport}
              mainDeparture={mainDeparture}
              mainArrival={mainArrival}
              errors={{
                carrier: errors[`${index}.carrier`],
                origin: errors[`${index}.origin`],
                destination: errors[`${index}.destination`],
                departure: errors[`${index}.departure`],
                arrival: errors[`${index}.arrival`],
                aircraft: errors[`${index}.aircraft`],
                flight: errors[`${index}.flight`],
              }}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
            />
          ))}

          <button
            type="button"
            onClick={handleAdd}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-amber-300 text-amber-600 font-semibold text-sm hover:bg-amber-50 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Segment
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-3.5 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <button
            onClick={handleConfirm}
            className="bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm px-6 py-2 rounded-lg transition-colors"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm px-5 py-2 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  },
);

export default IteneraryForm;
